import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createWhitelistFilter } from 'redux-persist-transform-filter';
import { RootState } from '../../store';
import { Color } from '../color/color';

export interface AuthState {
    uid: string | null;
    showUpgradePrompt: boolean;
    // Doc IDs of tiles painted while anonymous — persisted so they survive refresh
    anonymousTileIds: string[];
    // Assigned color identity
    assignedColor: string | null;
    assignedColorRgb: Color | null;
    colorRevealSeen: boolean;
    pendingServerSync: boolean;
}

const initialState: AuthState = {
    uid: null,
    showUpgradePrompt: false,
    anonymousTileIds: [],
    assignedColor: null,
    assignedColorRgb: null,
    colorRevealSeen: false,
    pendingServerSync: false,
};

export const sliceName = 'auth';

// Persist color identity fields alongside anonymousTileIds
export const persistFilter = createWhitelistFilter(sliceName, [
    'anonymousTileIds',
    'assignedColor',
    'assignedColorRgb',
    'colorRevealSeen',
    'pendingServerSync',
]);

export const selectUid = (state: RootState) => state.auth.uid;
export const selectShowUpgradePrompt = (state: RootState) => state.auth.showUpgradePrompt;
export const selectAnonymousTileIds = (state: RootState) => state.auth.anonymousTileIds;
export const selectAssignedColor = (state: RootState) => state.auth.assignedColor;
export const selectAssignedColorRgb = (state: RootState) => state.auth.assignedColorRgb;
export const selectColorRevealSeen = (state: RootState) => state.auth.colorRevealSeen;
export const selectPendingServerSync = (state: RootState) => state.auth.pendingServerSync;

export const authSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        setUid(state, action: PayloadAction<string | null>) {
            state.uid = action.payload;
        },
        triggerUpgradePrompt(state) {
            state.showUpgradePrompt = true;
        },
        dismissUpgradePrompt(state) {
            state.showUpgradePrompt = false;
        },
        addAnonymousTileIds(state, action: PayloadAction<string[]>) {
            const existing = new Set(state.anonymousTileIds);
            for (const id of action.payload) {
                existing.add(id);
            }
            state.anonymousTileIds = Array.from(existing);
        },
        clearAnonymousTileIds(state) {
            state.anonymousTileIds = [];
        },
        setAssignedColor(state, action: PayloadAction<{ color: string; rgb: Color }>) {
            state.assignedColor = action.payload.color;
            state.assignedColorRgb = action.payload.rgb;
        },
        setColorRevealSeen(state, action: PayloadAction<boolean>) {
            state.colorRevealSeen = action.payload;
        },
        setPendingServerSync(state, action: PayloadAction<boolean>) {
            state.pendingServerSync = action.payload;
        },
    },
});

export const {
    setUid,
    triggerUpgradePrompt,
    dismissUpgradePrompt,
    addAnonymousTileIds,
    clearAnonymousTileIds,
    setAssignedColor,
    setColorRevealSeen,
    setPendingServerSync,
} = authSlice.actions;

export default authSlice.reducer;
